'use client'

import * as React from 'react'
import { Check, CheckSquare, Heart, Images, Info, SearchCheck, Square, X } from 'lucide-react'
import { cn, formatPrice, proxyImageUrl } from '@/lib/utils'
import { PhotoBlock, isLikelyMonthlyPrice, paletteFor } from '@/components/roostr'
import { cardPriceRows, effectiveListingPrice } from '@/components/listings/listing-card'
import { AiSummaryButton } from '@/components/listing-detail'
import { useI18n } from '@/lib/i18n'
import type { Listing } from '@/lib/types'
import type { QueueItem } from './use-review-queue'

// NotifRow + CheckBtn — design notifications.jsx:99-155 ("What's new" inbox
// rows). One row per listing: leading checkbox (or status icon once acted),
// photo thumb + title/meta open the detail, source chip (desktop), relative
// time, trailing heart + mark-seen. Rows carry only ids until they scroll
// into view — an IntersectionObserver requests the per-listing hydration and
// the text/meta render as placeholder bars meanwhile (production adaptation
// of the prototype's always-hydrated mock data).
//
// Decision signals (user-directed 2026-07-16, "open / not open" upgrades):
// true cost (+utilities), a ✓/✗ diff of the listing against the SOURCE
// search's criteria + the user's must-haves (the decision-relevant tags; the
// rest collapse into "+N tags" with a tooltip), photo count on the thumb, and
// the on-demand AI summary popover.

/** Criteria fit for one listing vs its source search + must-haves. */
export interface RowCriteria {
  /** Keyword ids the source search filters on (∪ must-haves), deduped. */
  ids: string[]
  /** The user's must-have subset (drives the "x/y must-haves" summary). */
  mustHave: string[]
  label: (id: string) => string
}

const MAX_FIT_MARKS = 4
// Extra (non-criteria) tags render inline when at most this many; above it
// they collapse behind a click-to-expand "+N tags".
const INLINE_EXTRA_TAGS = 3

// Row price — the search card's semantics verbatim: effectiveListingPrice
// (rent + recurring extras = FULL monthly cost) as the number, hover (or
// click) opening the same cardPriceRows breakdown dropdown. Lives inside the
// open-detail button, so interactions stop propagation.
function RowPrice({ listing, mobile }: { listing: Listing; mobile: boolean }) {
  const { t } = useI18n()
  const [open, setOpen] = React.useState(false)
  const displayPrice = effectiveListingPrice(listing)
  const rows = cardPriceRows(listing, t)
  const hasBreakdown = rows.length > 1
  return (
    <span
      className="relative inline-flex items-baseline gap-[3px]"
      onMouseEnter={hasBreakdown ? () => setOpen(true) : undefined}
      onMouseLeave={hasBreakdown ? () => setOpen(false) : undefined}
      onClick={
        hasBreakdown
          ? (e) => {
              e.preventDefault()
              e.stopPropagation()
              setOpen((p) => !p)
            }
          : undefined
      }
      title={open || !hasBreakdown ? undefined : t('listingCard.costBreakdown', 'Cost breakdown')}
    >
      <span
        className={cn(
          'font-mono font-bold',
          displayPrice > 0 ? 'text-primary' : 'text-ink-muted',
          mobile ? 'text-[13.5px]' : 'text-[15px]'
        )}
      >
        {displayPrice > 0 ? formatPrice(displayPrice, listing.price_currency) : t('listingCard.noPrice', 'No price')}
      </span>
      {displayPrice > 0 && isLikelyMonthlyPrice(displayPrice) ? (
        <span className="text-[11.5px] text-ink-muted">{t('listingCard.perMo', '/mo')}</span>
      ) : null}
      {hasBreakdown ? <Info size={12} className="shrink-0 self-center text-ink-muted" /> : null}
      {open && hasBreakdown ? (
        <span className="absolute left-0 top-[calc(100%+6px)] z-[55] block w-[230px] animate-pop-in cursor-default rounded-shape-sm border border-outline bg-surface px-[13px] py-[5px] shadow-e3">
          {rows.map(([k, v], i) => (
            <span
              key={`${k}-${i}`}
              className={cn(
                'flex items-center justify-between gap-4 py-[6px]',
                i < rows.length - 1 && 'border-b border-outline-variant'
              )}
            >
              <span className="text-[12px] text-ink-secondary">{k}</span>
              <span className="font-mono text-[12px] font-semibold text-ink">{v}</span>
            </span>
          ))}
        </span>
      ) : null}
    </span>
  )
}

// Relative time — design data.jsx relTime, with a real clock + i18n.
export function relTime(iso: string, t: (k: string, f?: string) => string, uiLanguage: string): string {
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return ''
  const locale = uiLanguage === 'cs' ? 'cs-CZ' : 'en-US'
  const min = Math.max(0, Math.floor((Date.now() - then) / 60_000))
  if (min < 1) return t('notifications.justNow', 'Just now')
  if (min < 60) return `${min}m`
  const hr = Math.floor(min / 60)
  if (hr < 24) return `${hr}h`
  const d = Math.floor(hr / 24)
  if (d === 1) return t('notifications.yesterday', 'Yesterday')
  if (d < 7) return new Date(iso).toLocaleDateString(locale, { weekday: 'short' })
  return new Date(iso).toLocaleDateString(locale, { month: 'short', day: 'numeric' })
}

// Coarse group bucket for section headers — design data.jsx dayBucket.
export type DayBucket = 'today' | 'yesterday' | 'week' | 'earlier'
export function dayBucket(iso: string): DayBucket {
  const then = new Date(iso)
  const now = new Date()
  const sameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
  const yesterday = new Date(now)
  yesterday.setDate(now.getDate() - 1)
  if (sameDay(then, now)) return 'today'
  if (sameDay(then, yesterday)) return 'yesterday'
  if (now.getTime() - then.getTime() < 7 * 86_400_000) return 'week'
  return 'earlier'
}

// Checkbox button — design notifications.jsx:101-107.
export function CheckBtn({
  checked,
  onClick,
  title,
}: {
  checked: boolean
  onClick: () => void
  title?: string
}) {
  const { t } = useI18n()
  return (
    <button
      type="button"
      onClick={onClick}
      title={title || (checked ? t('notifications.deselect', 'Deselect') : t('notifications.select', 'Select'))}
      className="flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-full transition-colors hover:bg-surface-high"
    >
      {checked ? (
        <CheckSquare size={19} className="text-primary" />
      ) : (
        <Square size={19} className="text-ink-muted" />
      )}
    </button>
  )
}

export interface NotifRowProps {
  item: QueueItem
  liked: boolean
  selected: boolean
  onToggleSel: () => void
  onLike: () => void
  onSeen: () => void
  mobile: boolean
  /** Called when the row scrolls into view and has no listing yet. */
  requestHydration: (listingId: string) => void
  /** Source-search criteria + must-haves (undefined until searches load). */
  criteria?: RowCriteria
}

export function NotifRow({
  item,
  liked,
  selected,
  onToggleSel,
  onLike,
  onSeen,
  mobile,
  requestHydration,
  criteria,
}: NotifRowProps) {
  const { t, uiLanguage } = useI18n()
  const { listingId, source, at, listing, status } = item
  const rootRef = React.useRef<HTMLDivElement>(null)

  // Viewport hydration: fetch this row's listing when it becomes visible.
  React.useEffect(() => {
    if (listing || typeof IntersectionObserver === 'undefined') return
    const el = rootRef.current
    if (!el) return
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          requestHydration(listingId)
          io.disconnect()
        }
      },
      { rootMargin: '300px 0px' }
    )
    io.observe(el)
    return () => io.disconnect()
  }, [listing, listingId, requestHydration])

  // New tab keeps the inbox position intact (same affordance as ReviewCard).
  const openDetail = () => window.open(`/listings/${listingId}`, '_blank', 'noopener,noreferrer')

  const dim = status === 'seen' || status === 'skipped'
  const imageUrl = listing?.images?.[0]?.url ? proxyImageUrl(listing.images[0].url) : null
  const [imgBroken, setImgBroken] = React.useState(false)
  const photoCount = listing ? (listing.images?.length ?? 0) : null
  // Click-to-expand for the extra (non-criteria) tags when there are many.
  const [tagsOpen, setTagsOpen] = React.useState(false)

  // Criteria fit: ✓/✗ marks (all misses shown first-classed, matches fill the
  // rest of the cap), must-have score, and the remaining tags as "+N".
  const fit = React.useMemo(() => {
    if (!listing || !criteria || criteria.ids.length === 0) return null
    const kw = new Set(listing.keywords ?? [])
    const missing = criteria.ids.filter((id) => !kw.has(id))
    const matched = criteria.ids.filter((id) => kw.has(id))
    const shownMissing = missing.slice(0, MAX_FIT_MARKS)
    const shownMatched = matched.slice(0, Math.max(0, MAX_FIT_MARKS - shownMissing.length))
    const shown = new Set([...shownMatched, ...shownMissing])
    const extraTags = (listing.keywords ?? []).filter((id) => !shown.has(id))
    const mustHaveTotal = criteria.mustHave.length
    const mustHaveMet = criteria.mustHave.filter((id) => kw.has(id)).length
    return { shownMatched, shownMissing, extraTags, mustHaveMet, mustHaveTotal }
  }, [listing, criteria])

  // Layout / area / floor as first-class stats (user: "clearly visible") —
  // bold full-ink values instead of the old muted meta string.
  const statParts = listing
    ? ([
        listing.rooms != null ? `${listing.rooms}+kk` : null,
        listing.area != null ? `${listing.area} m²` : null,
        listing.floor != null
          ? `${listing.floor}${listing.total_floors ? `/${listing.total_floors}` : ''} ${t('notifications.floorAbbr', 'fl.')}`
          : null,
      ].filter(Boolean) as string[])
    : []

  return (
    <div
      ref={rootRef}
      className={cn(
        'flex items-center rounded-shape-md border border-outline-variant transition-[opacity,background-color] duration-200',
        mobile ? 'gap-[9px] px-[10px] py-[9px]' : 'gap-3 px-3 py-[9px]',
        selected ? 'bg-primary-container' : 'bg-surface',
        dim && 'opacity-55'
      )}
    >
      {status === null ? (
        <CheckBtn checked={selected} onClick={onToggleSel} />
      ) : (
        <span className="flex w-[30px] shrink-0 justify-center">
          {status === 'liked' ? (
            <Heart size={16} fill="currentColor" className="text-primary" />
          ) : (
            <Check size={16} className="text-ink-muted" />
          )}
        </span>
      )}
      <button
        type="button"
        onClick={openDetail}
        title={t('listingCard.openDetails', 'Open listing details')}
        className="shrink-0"
      >
        <span
          className={cn('relative block overflow-hidden rounded-lg', mobile ? 'h-[44px] w-[56px]' : 'h-[52px] w-[72px]')}
        >
          <PhotoBlock palette={paletteFor(listingId)} className="absolute inset-0 h-full w-full" />
          {imageUrl && !imgBroken ? (
            <img
              src={imageUrl}
              alt={listing?.title ?? ''}
              loading="lazy"
              onError={() => setImgBroken(true)}
              className="absolute inset-0 h-full w-full object-cover"
            />
          ) : null}
          {photoCount != null && !mobile ? (
            <span
              title={t('notifications.photoCountTip', 'Number of photos in the listing')}
              className="absolute bottom-[3px] right-[3px] inline-flex items-center gap-[3px] rounded bg-[rgba(26,23,23,0.7)] px-1 font-mono text-[9.5px] font-bold text-white"
            >
              <Images size={9} />
              {photoCount}
            </span>
          ) : null}
        </span>
      </button>
      <button
        type="button"
        onClick={openDetail}
        title={t('listingCard.openDetails', 'Open listing details')}
        className="min-w-0 flex-1 text-left"
      >
        {listing ? (
          <>
            {/* Two lines on mobile instead of one hard truncate: the column is
                only ~130px wide there, which cut Czech titles like "Pronájem
                bytu 2+kk 51m², balkón, Radimo…" to about 20 characters. */}
            <span
              className={cn(
                'block font-semibold text-ink',
                mobile ? 'line-clamp-2 text-[14px] leading-[1.25]' : 'truncate text-[13.5px]'
              )}
            >
              {listing.title}
            </span>
            <span className="mt-[2px] flex flex-wrap items-baseline gap-x-2 gap-y-[2px] text-[11.5px] text-ink-muted">
              {/* Full monthly price (search-card calculation) + hover/click
                  breakdown dropdown. */}
              <RowPrice listing={listing} mobile={mobile} />
              {/* Layout · area · floor — bold ink so they read at a glance. */}
              {statParts.map((s) => (
                <span key={s} className="whitespace-nowrap text-[12.5px] font-semibold text-ink">
                  {s}
                </span>
              ))}
              {listing.district || listing.city ? <span>{listing.district || listing.city}</span> : null}
            </span>
            {fit ? (
              <span className="mt-[4px] flex flex-wrap items-center gap-x-[10px] gap-y-[3px] text-[12px]">
                {/* Must-have score — same palette as the detail MustHavesCard:
                    green mono when every must-have is met, neutral secondary
                    otherwise (no amber; the search surfaces don't use it). */}
                {fit.mustHaveTotal > 0 ? (
                  <span
                    className={cn(
                      'font-mono font-bold',
                      fit.mustHaveMet === fit.mustHaveTotal ? 'text-success' : 'text-ink-secondary'
                    )}
                  >
                    {t('notifications.mustHavesMet', '{met}/{n} must-haves')
                      .replace('{met}', String(fit.mustHaveMet))
                      .replace('{n}', String(fit.mustHaveTotal))}
                  </span>
                ) : null}
                {/* Met = primary-container chip + check; missed = muted,
                    outlined, line-through — the MustHavesCard treatment. */}
                {fit.shownMatched.map((id) => (
                  <span
                    key={id}
                    className="inline-flex items-center gap-[3px] rounded-full bg-primary-container px-[8px] py-[2px] font-semibold text-on-primary-container"
                  >
                    <Check size={12} className="shrink-0" />
                    {criteria!.label(id)}
                  </span>
                ))}
                {fit.shownMissing.map((id) => (
                  <span
                    key={id}
                    className="inline-flex items-center gap-[3px] rounded-full border border-outline px-[8px] py-[2px] text-ink-muted line-through"
                  >
                    <X size={12} className="shrink-0" />
                    {criteria!.label(id)}
                  </span>
                ))}
                {/* Extra tags: inline when few; many collapse behind a
                    click-to-expand (user: "if too many -> click to show"). */}
                {fit.extraTags.length > 0 &&
                  (fit.extraTags.length <= INLINE_EXTRA_TAGS || tagsOpen ? (
                    <>
                      {fit.extraTags.map((id) => (
                        <span key={id} className="text-ink-muted">
                          {criteria!.label(id)}
                        </span>
                      ))}
                      {fit.extraTags.length > INLINE_EXTRA_TAGS ? (
                        <span
                          role="button"
                          onClick={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            setTagsOpen(false)
                          }}
                          className="cursor-pointer font-semibold text-ink-secondary hover:text-ink"
                        >
                          − {t('notifications.lessTags', 'less')}
                        </span>
                      ) : null}
                    </>
                  ) : (
                    <span
                      role="button"
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        setTagsOpen(true)
                      }}
                      className="cursor-pointer font-semibold text-ink-secondary underline decoration-dotted underline-offset-2 hover:text-ink"
                    >
                      {t('notifications.moreTags', '+{n} tags').replace('{n}', String(fit.extraTags.length))}
                    </span>
                  ))}
              </span>
            ) : null}
          </>
        ) : (
          <>
            <span className="block h-[13px] w-3/5 animate-pulse rounded bg-surface-high" />
            <span className="mt-[6px] block h-[10px] w-2/5 animate-pulse rounded bg-surface-high" />
          </>
        )}
      </button>
      {!mobile ? (
        <span className="inline-flex shrink-0 items-center gap-1 text-[11px] text-ink-muted">
          <SearchCheck size={13} className="shrink-0" />
          <span className="max-w-[150px] truncate">{source}</span>
        </span>
      ) : null}
      <span className="min-w-[34px] shrink-0 text-right font-mono text-[10.5px] text-ink-muted">
        {relTime(at, t, uiLanguage)}
      </span>
      <span className="flex shrink-0 gap-[2px]">
        {listing?.description && !mobile ? <AiSummaryButton listing={listing} variant="icon" /> : null}
        <button
          type="button"
          onClick={onLike}
          title={liked ? t('notifications.removeFromLiked', 'Remove from Liked') : t('notifications.saveToLiked', 'Save to Liked')}
          className="flex h-8 w-8 items-center justify-center rounded-full transition-colors hover:bg-surface-high"
        >
          <Heart size={17} fill={liked ? 'currentColor' : 'none'} className={liked ? 'text-primary' : 'text-ink-muted'} />
        </button>
        {status === null ? (
          <button
            type="button"
            onClick={onSeen}
            title={t('notifications.markSeenTip', 'Mark seen — clears it from the counter')}
            className="flex h-8 w-8 items-center justify-center rounded-full transition-colors hover:bg-surface-high"
          >
            <Check size={18} className="text-ink-muted" />
          </button>
        ) : null}
      </span>
    </div>
  )
}
